import { cn } from '~/common/utils/styleUtil';
import { formatPrice } from '~/common/utils/numberUtil';
import { formatDateByLanguage } from '~/common/utils/dateUtil';
import { useTranslation } from 'react-i18next';
import { isFuture } from 'date-fns';

export interface ArticlePriceProps {
  isAction: boolean;
  price: { normalPriceFranken: number; normalPriceRappen: string };
  oldPrice: number;
  unitText: string;
  actionValidFrom?: Date;
  actionValidTo?: Date;
  showOnlyActionPrice?: boolean;
  className?: string;
  size?: 'default' | 'lg';
  minimize?: boolean;
  showZero?: boolean;
  isHit?: boolean;
}

function ArticlePriceTag({
  price,
  unitText,
  isAction,
  isHit,
  isFutureAction,
  size
}: Readonly<{
  price: ArticlePriceProps['price'];
  unitText: string;
  isAction: boolean;
  isHit?: boolean;
  isFutureAction?: boolean;
  size: 'default' | 'lg';
}>) {
  const { i18n } = useTranslation();

  return (
    <div className={'flex'}>
      <div
        className={cn(
          `flex w-fit py-0.5 pl-1.5 rounded'`,
          isAction && !isHit && `bg-primary text-white rounded pr-2`,
          !isAction && 'pl-0',
          isFutureAction && 'bg-black text-white'
        )}
        data-testid={'article-price'}
      >
        <div className={`${size === 'lg' ? 'text-4xl' : 'text-3xl'} font-bold`}>
          {formatPrice(price.normalPriceFranken, i18n.language, 0)}
        </div>
        <div className={`${size === 'lg' ? 'text-xl' : 'text-base'} font-bold`}>
          {price.normalPriceRappen ? price.normalPriceRappen.padEnd(2, '0') : '00'}
        </div>
      </div>
      <div
        className={cn(
          `pl-1 pt-0.5 ${size === 'lg' ? 'text-xl' : 'text-base'} font-bold`,
          isAction && !isHit && !isFutureAction && 'text-primary'
        )}
      >
        {unitText ? `/ ${unitText}` : ''}
      </div>
    </div>
  );
}

function ArticleActionInfo({
  actionValidFrom,
  actionValidTo,
  isFutureAction,
  minimize,
  oldPrice
}: Readonly<{
  actionValidFrom?: Date;
  actionValidTo?: Date;
  isFutureAction?: boolean;
  minimize: boolean;
  oldPrice: number;
}>) {
  const { t, i18n } = useTranslation();

  return (
    <div className={cn('flex mt-1 text-sm gap-2', minimize ? 'flex-col xl:flex-row xl:gap-2' : 'xl:item-center')}>
      {actionValidFrom &&
        actionValidTo &&
        (isFutureAction ? (
          <div data-testid={'article-action-info-from-to'}>
            {t('in Aktion ab')}&nbsp;
            {formatDateByLanguage(new Date(actionValidFrom), i18n.language, 'dd.MM')}
          </div>
        ) : (
          <div className={'text-primary'} data-testid={'article-action-info-from-to'}>
            {t('in Aktion bis')}&nbsp;
            {formatDateByLanguage(new Date(actionValidTo), i18n.language, 'dd.MM')}
          </div>
        ))}

      {actionValidTo && !actionValidFrom && (
        <div className={'text-primary'} data-testid={'article-action-info-to'}>
          {t('in Aktion bis')}&nbsp;
          {formatDateByLanguage(new Date(actionValidTo), i18n.language, 'dd.MM')}
        </div>
      )}
      <div className={'line-through text-secondary'} data-testid={'article-action-oldprice'}>
        CHF&nbsp;{formatPrice(oldPrice, i18n.language)}
      </div>
    </div>
  );
}

export function ArticlePrice({
  price,
  oldPrice,
  isAction,
  unitText,
  actionValidFrom,
  actionValidTo,
  showOnlyActionPrice = false,
  className,
  size = 'default',
  showZero = false,
  minimize = false,
  isHit
}: Readonly<ArticlePriceProps>) {
  const isForFree = price.normalPriceFranken === 0 && price.normalPriceRappen === '00';
  if (isForFree && !showZero) {
    return <div />;
  }

  let showPrice = (showOnlyActionPrice && isAction) || showOnlyActionPrice === false;
  if (isHit) {
    showPrice = true;
  }

  if (!showPrice) {
    return <div className={'h-[40px]'} />;
  }

  const isFutureAction = actionValidFrom && isFuture(actionValidFrom);

  return (
    <div className={cn(className)}>
      <ArticlePriceTag
        price={price}
        unitText={unitText}
        isAction={isAction}
        isHit={isHit}
        isFutureAction={isFutureAction}
        size={size}
      />
      {isAction && !isHit && (
        <ArticleActionInfo
          actionValidFrom={actionValidFrom}
          actionValidTo={actionValidTo}
          isFutureAction={isFutureAction}
          minimize={minimize}
          oldPrice={oldPrice}
        />
      )}
    </div>
  );
}
